import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { Button } from "@/components/ui/button";
import useConfig from '@/hooks/useConfig';
import { useTranslation } from '@/hooks/useTranslation';
import { LangPicker } from '../langPicker/LangPicker';
import { useTheme } from '@/lib/ThemeProvider';
import { cn } from '@/lib/utils';
import { matchesSiteHeaderSolidBackground } from './siteHeaderAppearance';
import { SiteHeaderMobileNav } from './SiteHeaderMobileNav';
import { Bookmark, Search } from 'lucide-react';
import { useMobileHeaderPush } from './MobileHeaderPushContext';
import { SiteHeaderSearchDrawer } from './SiteHeaderSearchDrawer';
import { buildListingsSearchUrlFromLocation } from '@/widgets/listings/listingsUrlFilters';
const HOME_TOP_EXIT_THRESHOLD_PX = 600;
const HOME_TOP_ENTER_THRESHOLD_PX = 600;
export const SiteHeader = React.memo(() => {
    const { config, headerLinks, icons, links } = useConfig();
    const { t } = useTranslation();
    const [scrollY, setScrollY] = useState(0);
    const [isScrolled, setIsScrolled] = useState(false);
    const [mobileNavOpen, setMobileNavOpen] = useState(false);
    const [searchDrawerOpen, setSearchDrawerOpen] = useState(false);
    const { isHeaderHidden, headerHiddenSource } = useMobileHeaderPush();
    const navigate = useNavigate();
    const theme = useTheme();
    const { pathname } = useLocation();
    const [isHomeTopVisualState, setIsHomeTopVisualState] = useState(() => {
        if (typeof window === 'undefined') {
            return false;
        }
        if (pathname !== '/') {
            return false;
        }
        return window.scrollY <= HOME_TOP_EXIT_THRESHOLD_PX;
    });
    const isHomeRoot = pathname === '/';
    const isHomeTopState = isHomeRoot && isHomeTopVisualState;
    const isHomeScrolledState = isHomeRoot && !isHomeTopState;
    const solidBackground = useMemo(() => matchesSiteHeaderSolidBackground(pathname), [pathname]);
    const logoLink = `https://media.casalabia.dev${icons?.desktopHeaderLogo}`;
    // Throttled scroll handler для оптимизации производительности
    const handleScroll = useCallback(() => {
        const currentScrollY = window.scrollY;
        setScrollY(currentScrollY);
        setIsScrolled(currentScrollY > 50); // Начинаем блюрить после 50px
    }, []);
    // Throttle функция для ограничения частоты вызовов
    const throttle = useCallback((func, delay) => {
        let timeoutId;
        let lastExecTime = 0;
        return () => {
            const currentTime = Date.now();
            if (currentTime - lastExecTime > delay) {
                func();
                lastExecTime = currentTime;
            }
            else {
                clearTimeout(timeoutId);
                timeoutId = setTimeout(() => {
                    func();
                    lastExecTime = Date.now();
                }, delay - (currentTime - lastExecTime));
            }
        };
    }, []);
    const throttledHandleScroll = useCallback(throttle(handleScroll, 16), // ~60fps
    [handleScroll, throttle]);
    useEffect(() => {
        // Добавляем обработчик события скролла
        window.addEventListener('scroll', throttledHandleScroll, { passive: true });
        // Устанавливаем начальное состояние
        handleScroll();
        // Cleanup при размонтировании компонента
        return () => {
            window.removeEventListener('scroll', throttledHandleScroll);
        };
    }, [throttledHandleScroll, handleScroll]);
    useEffect(() => {
        if (!isHomeRoot) {
            setIsHomeTopVisualState(false);
            return;
        }
        setIsHomeTopVisualState((prev) => {
            if (prev) {
                return scrollY <= HOME_TOP_EXIT_THRESHOLD_PX;
            }
            return scrollY <= HOME_TOP_ENTER_THRESHOLD_PX;
        });
    }, [isHomeRoot, scrollY]);
    // Вычисляем степень блюра на основе позиции скролла
    const blurAmount = Math.min(scrollY / 100, 1); // Максимальный блюр при 100px скролла
    const backgroundOpacity = Math.min(scrollY / 100, 0.95); // Максимальная непрозрачность фона при 100px
    const isDark = theme.isDark;
    const glassHeaderStyle = useMemo(() => ({
        backdropFilter: `blur(${blurAmount * 12}px)`,
        WebkitBackdropFilter: `blur(${blurAmount * 12}px)`,
        backgroundColor: `${isDark ? `rgba(0, 0, 0, ${backgroundOpacity * 0.1})` : `rgba(255, 255, 255, ${backgroundOpacity * 0.1})`}`,
        // boxShadow: isScrolled ? '0 2px 20px rgba(0, 0, 0, 0.05)' : 'none',
    }), [blurAmount, backgroundOpacity, isDark, isScrolled]);
    const solidHeaderStyle = useMemo(() => ({
        boxShadow: 'none',
    }), [isScrolled]);
    const homeScrolledHeaderStyle = useMemo(() => ({
        backgroundColor: 'rgb(255, 255, 255)',
        backdropFilter: 'none',
        WebkitBackdropFilter: 'none',
        boxShadow: 'none',
    }), []);
    const headerSurfaceStyle = mobileNavOpen
        ? undefined
        : isHomeTopState
            ? glassHeaderStyle
            : isHomeScrolledState
                ? homeScrolledHeaderStyle
                : (solidBackground ? solidHeaderStyle : glassHeaderStyle);
    const hideHeader = isHeaderHidden && !mobileNavOpen;
    const headerStyle = useMemo(() => ({
        ...headerSurfaceStyle,
        transform: hideHeader ? 'translate3d(0, calc(-1 * var(--header-height)), 0)' : 'translate3d(0, 0, 0)',
    }), [headerSurfaceStyle, hideHeader]);
    const isLightControls = isHomeTopState && !mobileNavOpen;
    const navLinkButtonClassName = isLightControls
        ? '!text-white hover:!text-white/80'
        : isHomeScrolledState
            ? '!text-black hover:!text-black/75'
            : '';
    const ghostButtonClassName = isLightControls
        ? '!text-white hover:!text-white hover:bg-white/10'
        : isHomeScrolledState
            ? '!text-black hover:!text-black hover:bg-black/5'
            : '';
    const mobileTitleClassName = isLightControls
        ? 'text-white'
        : isHomeScrolledState
            ? 'text-black'
            : '';
    const openSearchDrawer = useCallback(() => {
        setSearchDrawerOpen(true);
    }, []);
    const handleSearchLocationSelect = useCallback((location) => {
        const listingsPath = links?.listings ?? '/listings';
        const href = buildListingsSearchUrlFromLocation(listingsPath, location);
        setSearchDrawerOpen(false);
        navigate(href);
    }, [links?.listings, navigate]);
    return (_jsxs(_Fragment, { children: [_jsx("header", { className: cn('fixed top-0 left-0 right-0 z-50 flex flex-col justify-center h-(--header-height) mx-auto shrink-0 items-center gap-2 transition-all duration-200 ease-out group-has-data-[collapsible=icon]/sidebar-wrapper:h-(--header-height)', 'bg-background'), style: headerStyle, "data-header-hidden-source": headerHiddenSource, "data-mobile-header-hidden-source": headerHiddenSource, children: _jsxs("div", { className: "relative flex w-full max-w-(--max-page-width) items-center justify-between gap-1 lg:gap-2 lg:px-6", children: [_jsx("div", { className: 'flex md:hidden items-center', children: _jsx(SiteHeaderMobileNav, { links: headerLinks, onOpenChange: setMobileNavOpen, onSearchRequest: openSearchDrawer, triggerClassName: ghostButtonClassName }) }), _jsx(Link, { to: "/", className: "hidden md:block mr-2 pr-4", children: _jsx("img", { src: logoLink, alt: "Logo", className: "h-10", style: { filter: 'grayscale(100%)' } }) }), _jsx("div", { className: "hidden md:flex items-center gap-6", children: headerLinks.map((link) => (_jsx(Button, { variant: "link", asChild: true, size: "sm", className: cn('hidden sm:flex hover:font-medium', navLinkButtonClassName), children: _jsx(Link, { to: link.path, className: 'text-sm font-normal', children: t(link.caption) }) }, link.path))) }), _jsx("div", { className: "absolute left-[88px] right-[88px] top-0 bottom-0 flex md:hidden items-center justify-center", children: _jsx(Link, { to: "/", className: "", children: _jsx("span", { className: cn('text-sm uppercase font-normal', mobileTitleClassName), children: config.displayName }) }) }), _jsxs("div", { className: "flex md:hidden items-center gap-1 mr-2", children: [_jsx(Button, { variant: "ghost", size: "icon", className: ghostButtonClassName, onClick: openSearchDrawer, "aria-label": t({
                                        en: 'Open search',
                                        de: 'Suche öffnen',
                                        it: 'Apri ricerca',
                                    }), children: _jsx(Search, { className: "size-5 stroke-1" }) }), _jsx(Button, { variant: "ghost", size: "icon", className: ghostButtonClassName, children: _jsx(Bookmark, { className: "size-5 stroke-1" }) })] }), _jsxs("div", { className: "hidden md:flex items-center gap-1 mr-2", children: [_jsx(Button, { variant: "ghost", size: "icon", className: ghostButtonClassName, onClick: openSearchDrawer, "aria-label": t({
                                        en: 'Open search',
                                        de: 'Suche öffnen',
                                        it: 'Apri ricerca',
                                    }), children: _jsx(Search, { className: "size-4 stroke-1" }) }), config && config.common.languages && config.common.languages.length > 0 && (_jsx("div", { className: "flex items-center gap-2", children: _jsx(LangPicker, { triggerClassName: ghostButtonClassName }) }))] })] }) }), _jsx(SiteHeaderSearchDrawer, { open: searchDrawerOpen, onOpenChange: setSearchDrawerOpen, onSelectLocation: handleSearchLocationSelect })] }));
});
